import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './Revvy.css';

// Customize Revvy's message based on scan context
// This function enhances the base message from the database with dynamic scan results
const getCustomizedMessage = (baseMessage, scanContext, stateId) => {
  // For post-scan state, enhance the message with scan results
  if (stateId === 'prerev_adsense_post_scan' && scanContext?.hasScanResults) {
    const { score, statusCounts, isReady } = scanContext;
    const issuesCount = (statusCounts?.fail || 0) + (statusCounts?.warning || 0);
    
    if (isReady) {
      return `🎉 Excellent news! Your site scored ${score}% and looks ready for AdSense approval. All critical checks passed. You're in great shape to apply to Google AdSense! Once you're approved, create a free account and I'll help you optimize your revenue.`;
    } else if (issuesCount === 0) {
      return `Great work! Your site scored ${score}% on the AdSense readiness check. Everything looks good - you're ready to apply! After approval, connect your AdSense account and I'll help you maximize your earnings.`;
    } else {
      const failCount = statusCounts?.fail || 0;
      const warningCount = statusCounts?.warning || 0;
      let message = `I've analyzed your site and found ${issuesCount} issue${issuesCount > 1 ? 's' : ''} that need attention. `;
      
      if (failCount > 0) {
        message += `${failCount} critical issue${failCount > 1 ? 's' : ''} must be fixed before applying. `;
      }
      if (warningCount > 0) {
        message += `${warningCount} warning${warningCount > 1 ? 's' : ''} should be addressed to improve your approval chances. `;
      }
      
      message += `Your current score is ${score}%. Scroll down to see the details, and let's fix these together!`;
      return message;
    }
  }

  // For other states, use the base message as-is
  return baseMessage || "Hey there! I'm Revvy, your revenue engine. 👋";
};

const Revvy = ({ initialStateId, onStateChange, className = '', scanContext = null }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchState = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams();
      if (initialStateId) {
        params.append('stateId', initialStateId);
      }

      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(`/api/revvy/state?${params}`, { headers });
      setState(response.data.state);
      
      if (onStateChange) {
        onStateChange(response.data.state);
      }
    } catch (err) {
      console.error('Error fetching Revvy state:', err);
      setError('Failed to load Revvy');
    } finally {
      setLoading(false);
    }
  }, [initialStateId, token, onStateChange]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  const handleAction = (action) => {
    switch (action.type) {
      case 'navigation':
        if (action.target.startsWith('/')) {
          navigate(action.target);
        } else {
          // Handle internal navigation targets
          console.log('Navigate to:', action.target);
        }
        break;
      case 'cta':
        if (action.target.startsWith('/')) {
          navigate(action.target);
        }
        break;
      case 'link':
        window.open(action.target, '_blank');
        break;
      default:
        console.log('Action:', action);
    }
  };

  if (loading && !state) {
    return (
      <div className={`revvy-container ${className}`}>
        <div className="revvy-loading">
          <div className="revvy-avatar">
            <img src="/images/Revvy The Rev Engine.webp" alt="Revvy" />
          </div>
          <p>Revvy is thinking...</p>
        </div>
      </div>
    );
  }

  if (error && !state) {
    return null; // Fail silently
  }

  if (!state) {
    return null;
  }

  // Generate action items dynamically from scan results
  // Action items are only shown for checks that failed or have warnings
  const getActionItems = () => {
    if (!scanContext?.hasScanResults || !scanContext.checks || !Array.isArray(scanContext.checks)) {
      return [];
    }

    const actionItems = [];
    const { checks } = scanContext;
    
    // Map check IDs to user-friendly action item labels
    const checkActionMap = {
      'ads-txt': { label: 'Fix my ads.txt file' },
      'robots-txt': { label: 'Fix my robots.txt file' },
      'sitemap': { label: 'Fix my sitemap' },
      'privacy-policy': { label: 'Add Privacy Policy page' },
      'contact-page': { label: 'Add Contact page' },
      'about-page': { label: 'Add About Us page' },
      'navigation-structure': { label: 'Improve Navigation Structure' },
      'content-depth': { label: 'Improve Content Depth & Quality' },
      'image-usage': { label: 'Improve Image Usage' },
      'https': { label: 'Fix HTTPS configuration' },
      'cmp': { label: 'Fix Consent Management Platform' },
      'ad-tag-security': { label: 'Fix Ad Tag Security issues' },
    };

    // Only show action items for checks that failed or have warnings
    checks.forEach(check => {
      if (check && (check.status === 'fail' || check.status === 'warning')) {
        const actionMap = checkActionMap[check.id];
        if (actionMap) {
          actionItems.push({
            label: actionMap.label,
            type: 'action-item',
            target: `#check-${check.id}`,
            checkId: check.id,
            status: check.status,
            title: check.title || check.id
          });
        } else {
          // Fallback for any check not in the map
          actionItems.push({
            label: `Fix ${check.title || check.id}`,
            type: 'action-item',
            target: `#check-${check.id}`,
            checkId: check.id,
            status: check.status,
            title: check.title || check.id
          });
        }
      }
    });

    return actionItems;
  };

  const actionItems = getActionItems();
  const shouldShowActionItems = scanContext?.hasScanResults && actionItems.length > 0;

  return (
    <div className={`revvy-container ${className}`}>
      <div className="revvy-card">
        <div className="revvy-header">
          <div className="revvy-avatar">
            <img src="/images/Revvy The Rev Engine.webp" alt="Revvy" />
          </div>
          <div className="revvy-title">
            <h3>Revvy</h3>
            <span className="revvy-subtitle">Your Revenue Engine</span>
          </div>
        </div>

        <div className="revvy-message">
          <p>{getCustomizedMessage(state.revvy_message, scanContext, state.id)}</p>
        </div>

        {shouldShowActionItems && (
          <div className="revvy-action-items">
            <div className="revvy-action-items-header">
              <strong>Action Items</strong>
              <span className="revvy-action-items-count">{actionItems.length} issue{actionItems.length !== 1 ? 's' : ''} to fix</span>
            </div>
            <div className="revvy-action-items-list">
              {actionItems.map((actionItem, index) => (
                <button
                  key={index}
                  className={`revvy-action-item-btn revvy-action-item-${actionItem.status}`}
                  onClick={() => handleAction(actionItem)}
                >
                  <span className="revvy-action-item-icon">
                    {actionItem.status === 'fail' ? '🔴' : '🟡'}
                  </span>
                  {actionItem.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="revvy-loading-overlay">
            <div className="revvy-spinner"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Revvy;
